/**
 * useUrbanEntities — Hook de datos para urban_entities
 *
 * @remarks
 * Carga la lista de urban_entities del proyecto activo, respetando los
 * filtros del store. Recarga cuando cambian projectId o filtros.
 * Patrón manual (sin React Query ni SWR) igual al de waste/mobility.
 *
 * @module urban-inventory/hooks
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { urbanEntitiesApi, getUrbanEntitiesApiError } from '../services/urban-entities-api.service';
import { useUrbanInventoryStore } from '../store/urban-inventory-store';
import type { UrbanEntity } from '../types/urban-entity.types';

interface UseUrbanEntitiesOptions {
  projectId: string;
  /** Si false, no carga automáticamente. Default: true */
  autoLoad?: boolean;
}

export interface UseUrbanEntitiesResult {
  entities: UrbanEntity[];
  filteredEntities: UrbanEntity[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Tipos de entidad presentes en los datos cargados */
  detectedEntityTypes: string[];
}

export function useUrbanEntities({
  projectId,
  autoLoad = true,
}: UseUrbanEntitiesOptions): UseUrbanEntitiesResult {
  const [entities, setEntities] = useState<UrbanEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeEntityTypes = useUrbanInventoryStore((s) => s.activeEntityTypes);
  const statusFilter = useUrbanInventoryStore((s) => s.statusFilter);

  // Ref para evitar race conditions si el projectId cambia mientras carga
  const loadingProjectRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;

    loadingProjectRef.current = projectId;
    setIsLoading(true);
    setError(null);

    try {
      // Traemos todo del backend y filtramos en cliente.
      // Con filtros simples (un entityType, un status), podría filtrarse en server,
      // pero la API solo acepta un entityType a la vez; filtrar en cliente es más flexible
      // para la UI de chips múltiples.
      const data = await urbanEntitiesApi.list(projectId);

      // Solo aplicar si el projectId sigue siendo el mismo
      if (loadingProjectRef.current === projectId) {
        setEntities(data);
      }
    } catch (err) {
      if (loadingProjectRef.current === projectId) {
        setError(getUrbanEntitiesApiError(err));
      }
    } finally {
      if (loadingProjectRef.current === projectId) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (autoLoad && projectId) {
      void load();
    }
  }, [autoLoad, projectId, load]);

  // Filtrado en cliente: respeta activeEntityTypes y statusFilter
  const filteredEntities = useMemo(() => {
    let result = entities;

    if (activeEntityTypes.length > 0) {
      result = result.filter((e) => activeEntityTypes.includes(e.entityType));
    }

    if (statusFilter) {
      result = result.filter((e) => e.status === statusFilter);
    }

    return result;
  }, [entities, activeEntityTypes, statusFilter]);

  // Tipos detectados en los datos cargados (para los chips de filtro)
  const detectedEntityTypes = useMemo(() => {
    const types = new Set(entities.map((e) => e.entityType));
    return Array.from(types).sort();
  }, [entities]);

  return {
    entities,
    filteredEntities,
    isLoading,
    error,
    reload: load,
    detectedEntityTypes,
  };
}
